import React, { useContext, useEffect, useState } from 'react';
import { createStyles, FormControlLabel, Grid, makeStyles, MenuItem, TextField, Theme, Typography, withStyles } from '@material-ui/core';
import Switch, { SwitchClassKey, SwitchProps } from '@material-ui/core/Switch';
import { grey } from '@material-ui/core/colors';
import axios, { CancelTokenSource } from 'axios';

import { GET_SETTING_UPDATE_BASE_URL } from 'constants/url';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { TabNavPreference, useTabNavPreference } from 'contexts/TabNavSettingContext';

interface PageProps {
  settingInAppTabNav: SettingModel;
  setSettingInAppTabNav: React.Dispatch<React.SetStateAction<SettingModel>>;
  handleOpenSnackbar: (type: 'success' | 'error', message: string) => void;
}

interface Styles extends Partial<Record<SwitchClassKey, string>> {
  focusVisible?: string;
}

interface Props extends SwitchProps {
  classes: Styles;
}

const IOSSwitch = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 42,
      height: 26,
      padding: 0,
      margin: theme.spacing(1)
    },
    switchBase: {
      padding: 1,
      '&$checked': {
        transform: 'translateX(16px)',
        color: theme.palette.common.white,
        '& + $track': {
          backgroundColor: '#53A0BE',
          opacity: 1,
          border: 'none'
        }
      },
      '&$focusVisible $thumb': {
        color: '#53A0BE',
        border: '6px solid #fff'
      }
    },
    thumb: {
      width: 24,
      height: 24
    },
    track: {
      borderRadius: 26 / 2,
      border: `1px solid ${theme.palette.grey[400]}`,
      backgroundColor: theme.palette.grey[50],
      opacity: 1,
      transition: theme.transitions.create(['background-color', 'border'])
    },
    checked: {},
    focusVisible: {}
  })
)(({ classes, ...props }: Props) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked
      }}
      {...props}
    />
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  secondText: {
    color: grey[500]
  },
  scopeNote: {
    color: grey[500],
    marginTop: theme.spacing(1)
  },
  /**
   * The switch and the select are laid out as the same three parts — caption, control, helper — so
   * they line up. That is also why the select draws no floating label of its own: a label sitting on
   * the input's border can never sit level with a caption above a switch.
   */
  controlLabel: {
    display: 'block',
    color: grey[600],
    marginBottom: theme.spacing(0.5)
  },
  /** One band both controls sit in, tall enough for the switch, which is the taller of the two. */
  controlBody: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 42
  },
  switchControl: {
    marginLeft: -theme.spacing(1)
  },
  controlHelper: {
    display: 'block',
    color: grey[500],
    marginTop: theme.spacing(0.5)
  },
  select: {
    minWidth: 220
  }
}));

const PREFERENCE_LABEL: Record<TabNavPreference, string> = {
  default: 'Follow company setting',
  on: 'Always on for me',
  off: 'Always off for me'
};

/**
 * Two controls, two scopes.
 *
 * The switch is the company default, guarded by reaching this page at all — the same gate every
 * other setting here uses. The dropdown below it is this user's own choice in this browser, and it
 * wins over the company default, so someone who prefers the old behaviour can keep it.
 */
const InAppTabNav: React.FC<PageProps> = props => {
  const { settingInAppTabNav, setSettingInAppTabNav, handleOpenSnackbar } = props;
  const classes = useStyles();
  const { preference, setPreference } = useTabNavPreference();
  const { updateCurrentUser } = useContext(CurrentUserContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isCompanyActive, setCompanyActive] = useState<boolean>(false);

  useEffect(() => {
    setCompanyActive(settingInAppTabNav.isActive);
  }, [settingInAppTabNav]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    setCompanyActive(nextValue);
    setLoading(true);

    try {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      const { data } = await axios.put(
        `${GET_SETTING_UPDATE_BASE_URL(settingInAppTabNav!.id)}`,
        { isActive: nextValue },
        { cancelToken: cancelTokenSource.token }
      );

      setSettingInAppTabNav(data);
      setCompanyActive(data.isActive);

      // The company default reaches the rest of the app on the current user payload, which was built
      // at sign in. Without this the bar only changes on the next reload.
      updateCurrentUser({ inAppTabNav: data.isActive });
      handleOpenSnackbar('success', 'Successfully updated setting data.');
    } catch (err) {
      console.log(err);
      // Put the switch back where it was, so it never claims a change that did not save.
      setCompanyActive(!nextValue);
      handleOpenSnackbar('error', 'Error update setting data.');
    }

    setLoading(false);
  };

  return (
    <>
      <Grid item xs={6}>
        <Typography variant='h5'>In-App Tab Bar</Typography>
        <Typography variant='body1' className={classes.secondText}>
          Open quotations, jobs, clients and invoices as tabs inside the web app instead of new browser tabs. Closing a tab discards any unsaved
          changes in it.
        </Typography>
        {/* Each control says who it affects; only the order between them still needs saying. */}
        <Typography variant='body2' className={classes.scopeNote}>
          The switch sets the company default. Your own preference overrides it.
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Grid container spacing={4} alignItems='flex-start'>
          <Grid item>
            <Typography variant='caption' className={classes.controlLabel}>
              Company default
            </Typography>
            <div className={classes.controlBody}>
              <FormControlLabel
                className={classes.switchControl}
                control={<IOSSwitch checked={isCompanyActive} onChange={handleChange} name='inAppTabNav' />}
                disabled={isLoading}
                labelPlacement='end'
                label={isCompanyActive ? 'Active' : 'Inactive'}
              />
            </div>
            <Typography variant='caption' className={classes.controlHelper}>
              Applies to everyone in the company.
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant='caption' className={classes.controlLabel}>
              My preference
            </Typography>
            <div className={classes.controlBody}>
              <TextField
                select
                size='small'
                variant='outlined'
                className={classes.select}
                value={preference}
                onChange={event => setPreference(event.target.value as TabNavPreference)}
                inputProps={{ 'aria-label': 'My preference' }}
              >
                {(Object.keys(PREFERENCE_LABEL) as TabNavPreference[]).map(option => (
                  <MenuItem key={option} value={option}>
                    {PREFERENCE_LABEL[option]}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <Typography variant='caption' className={classes.controlHelper}>
              Applies to you, on this browser only.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default InAppTabNav;
